import * as courseAPI from '@adobe/edex/frontend/api/course';
import store from '@adobe/edex/frontend/store';
import { getCourseHeroImageURL } from '@adobe/edex/ui/shared/frontend/utils';
import { COURSE_TYPE_ID_TO_PATH } from '@adobe/edex/ui/shared/constants';

const getDefaultState = () => ({
  course: null,
  enrollment: null,
  nextStep: null,
  nextRegularStep: null,
  stepsByID: {},
  stepsByIndex: {},
  workshopsByStepID: {},
  staff: null,
  instructors: null,
  upcoming: [],
  hasLiveClasses: false,
  related: [],
});

function stepIsEnabled(step, nextStep, workshop, course) {
  if (store.state.member.member?.isAdmin) {
    return true;
  } // TODO: dirty hack (+prone to race condition) - refactor
  if (!course.meta.started) {
    return false;
  }
  if (!workshop.meta.started) {
    return false;
  }

  if (step.type === 'assignment' || step.type === 'final-assignment') {
    if (
      course.educators?.instructors?.includes(store.state.member.member?.id) ||
      course.educators?.staff?.includes(store.state.member.member?.id)
    ) {
      return true;
    }
    if (step.meta.assignmentID) {
      return true;
    }
    if (nextStep?.id === step.id) {
      return true;
    }
    return false;
  }

  return true;
}

const course = {
  namespaced: true,
  state: getDefaultState(),
  getters: {
    nextStepRouteParams: ({ course, workshopsByStepID, nextStep }) => {
      if (!nextStep) {
        return {};
      }

      const workshop = workshopsByStepID[nextStep.id];
      return {
        progressIndex: nextStep.index + 1,
        vanityURL: course.vanityURL,
        workshopVanityURL: workshop.vanityURL,
      };
    },
  },
  mutations: {
    setCourse(state, course) {
      let nextStep = null;
      let nextRegularStep = null;
      const stepsByID = {};
      const stepsByIndex = {};
      const workshopsByStepID = {};

      course.workshops.forEach((workshop) => {
        workshop.sections.forEach((section) => {
          section.steps.forEach((step) => {
            const index = Object.keys(stepsByIndex).length;
            step.index = index;

            stepsByID[step.id] = step;
            stepsByIndex[index] = step;
            workshopsByStepID[step.id] = workshop;

            if (!nextStep && !step.meta.completed) {
              nextStep = step;
            }
            if (!nextRegularStep && step.type === 'regular' && !step.meta.completed) {
              nextRegularStep = step;
            }
          });
        });

        workshop.hasLiveClasses = workshop.liveClasses.length > 0;
        workshop.hasLiveClassesUpcoming = false;
        workshop.liveClasses.forEach((liveClass) => {
          const now = new Date();
          const classTime = new Date(liveClass.time);
          liveClass.isUpcoming = classTime >= now;
          if (workshop.hasLiveClassesUpcoming === false && liveClass.isUpcoming) {
            workshop.hasLiveClassesUpcoming = true;
          }
        });
      });

      // second iteration to calc `enabled` flag
      course.workshops.forEach((workshop) => {
        workshop.sections.forEach((section) => {
          section.steps.forEach((step) => {
            step.enabled = stepIsEnabled(step, nextStep, workshop, course);
          });
        });
      });

      course.heroImageURL = getCourseHeroImageURL(course);

      state.nextStep = nextStep;
      state.nextRegularStep = nextRegularStep;
      state.stepsByID = stepsByIndex;
      state.stepsByIndex = stepsByIndex;
      state.workshopsByStepID = workshopsByStepID;

      state.course = course;
      state.enrollment = course.meta.enrollment;
      state.hasLiveClasses = course.workshops.some((workshop) => workshop.liveClasses.length > 0);
    },
    setCourseEducators(state, educators) {
      state.instructors = educators.instructors;
      state.staff = educators.staff;
    },
    resetState(state) {
      Object.assign(state, getDefaultState());
    },
  },
  actions: {
    async fetchCourse({ commit, state }, id) {
      const { data } = await courseAPI.fetchCourse(id);
      commit('setCourse', data);
    },
    async fetchCourseByIdAndType({ commit, state }, { id, courseTypeRoutePath }) {
      const { data } = await courseAPI.fetchCourse(id);
      const courseTypeMismatch = COURSE_TYPE_ID_TO_PATH.get(data?.courseType?.id) !== courseTypeRoutePath;
      if (!courseTypeMismatch) {
        commit('setCourse', data);
      }
    },
    enroll({ commit, state }, id) {
      return courseAPI.enroll(id);
    },
    unenroll({ commit, state }, id) {
      return courseAPI.unenroll(id);
    },
    async getCourseEducators({ commit }, id) {
      const { status, data } = await courseAPI.educators(id);
      if (status === 200) {
        commit('setCourseEducators', data);
      }
    },
    completeStep({ commit, state }, { courseID, workshopID, workshopStepID }) {
      return courseAPI.completeStep(courseID, workshopID, workshopStepID);
    },
    updateCourse({ commit }, data) {
      commit('resetState');
      commit('setCourse', data);
    },
    resetCourse({ commit }) {
      commit('resetState');
    },
  },
};

export default course;
